/**
 * Field validation utilities to ensure proper field mapping and highlighting
 */

import { ExtractedField } from '../types';

/**
 * Validates that a field has appropriate bounding boxes for its type
 */
export function validateFieldBoundingBoxes(field: ExtractedField): {
  isValid: boolean;
  issues: string[];
  suggestions: string[];
} {
  const issues: string[] = [];
  const suggestions: string[] = [];

  // Check if field has bounding boxes
  if (!field.boundingBoxes || field.boundingBoxes.length === 0) {
    issues.push(`Field ${field.name} has no bounding boxes for highlighting`);
    suggestions.push('Verify field extraction model includes coordinate data');
    return { isValid: false, issues, suggestions };
  }

  // Validate bounding box coordinates
  field.boundingBoxes.forEach((bbox, index) => {
    if (bbox.x < 0 || bbox.x > 1 || bbox.y < 0 || bbox.y > 1) {
      issues.push(`Field ${field.name} bounding box ${index} has invalid coordinates (${bbox.x}, ${bbox.y})`);
    }
    
    if (bbox.width <= 0 || bbox.height <= 0) {
      issues.push(`Field ${field.name} bounding box ${index} has invalid dimensions (${bbox.width}x${bbox.height})`);
    }
    
    if (bbox.page < 1) {
      issues.push(`Field ${field.name} bounding box ${index} has invalid page number (${bbox.page})`);
    }
  });

  // Field-specific validations
  switch (field.name) {
    case 'saleDate':
      // Sale date should not share coordinates with recording date
      if (field.boundingBoxes.length > 4) {
        issues.push(`Sale date has too many bounding boxes (${field.boundingBoxes.length}), expected 1-4`);
        suggestions.push('Check field consolidation logic for sale date extraction');
      }
      break;
      
    case 'recordingDate':
      // Recording date should have exactly one bounding box
      if (field.boundingBoxes.length !== 1) {
        issues.push(`Recording date should have exactly 1 bounding box, found ${field.boundingBoxes.length}`);
        suggestions.push('Verify recording date extraction model configuration');
      }
      break;
      
    case 'legalDescription': {
      // Legal description can span multiple pages
      const pages = new Set(field.boundingBoxes.map(bbox => bbox.page));
      if (pages.size > 3) {
        issues.push(`Legal description spans ${pages.size} pages, which may be excessive`);
        suggestions.push('Review legal description extraction boundaries');
      }
      break;
    }
  }

  return {
    isValid: issues.length === 0,
    issues,
    suggestions
  };
}

/**
 * Detects potential field mapping conflicts
 */
export function detectFieldMappingConflicts(fields: ExtractedField[]): {
  conflicts: Array<{
    field1: string;
    field2: string;
    issue: string;
    severity: 'warning' | 'error';
  }>;
} {
  const conflicts: Array<{
    field1: string;
    field2: string;
    issue: string;
    severity: 'warning' | 'error';
  }> = [];

  // Check for overlapping bounding boxes between different fields
  for (let i = 0; i < fields.length; i++) {
    for (let j = i + 1; j < fields.length; j++) {
      const field1 = fields[i];
      const field2 = fields[j];
      
      // Skip if either field has no bounding boxes
      if (!field1.boundingBoxes?.length || !field2.boundingBoxes?.length) continue;
      
      // Check for exact coordinate matches (potential mapping error)
      const hasExactMatch = field1.boundingBoxes.some(bbox1 =>
        field2.boundingBoxes!.some(bbox2 =>
          bbox1.page === bbox2.page &&
          Math.abs(bbox1.x - bbox2.x) < 0.001 &&
          Math.abs(bbox1.y - bbox2.y) < 0.001 &&
          Math.abs(bbox1.width - bbox2.width) < 0.001 &&
          Math.abs(bbox1.height - bbox2.height) < 0.001
        )
      );
      
      if (hasExactMatch) {
        conflicts.push({
          field1: field1.name,
          field2: field2.name,
          issue: 'Fields share identical bounding box coordinates',
          severity: 'error'
        });
      }
      
      // Special case: saleDate and recordingDate should never overlap
      if ((field1.name === 'saleDate' && field2.name === 'recordingDate') ||
          (field1.name === 'recordingDate' && field2.name === 'saleDate')) {
        
        const hasOverlap = field1.boundingBoxes.some(bbox1 =>
          field2.boundingBoxes!.some(bbox2 =>
            bbox1.page === bbox2.page &&
            bbox1.x < bbox2.x + bbox2.width &&
            bbox1.x + bbox1.width > bbox2.x &&
            bbox1.y < bbox2.y + bbox2.height &&
            bbox1.y + bbox1.height > bbox2.y
          )
        );
        
        if (hasOverlap) {
          conflicts.push({
            field1: field1.name,
            field2: field2.name,
            issue: 'Sale date and recording date have overlapping coordinates',
            severity: 'warning'
          });
        }
      }
    }
  }

  return { conflicts };
}

/**
 * Logs field validation results for debugging
 */
export function logFieldValidation(fields: ExtractedField[]): void {
  console.group('🔍 Field Validation Report');
  
  fields.forEach(field => {
    const validation = validateFieldBoundingBoxes(field);
    
    if (!validation.isValid) {
      console.warn(`❌ ${field.name}:`, validation.issues);
      if (validation.suggestions.length > 0) {
        console.info(`💡 Suggestions:`, validation.suggestions);
      }
    } else {
      console.log(`✅ ${field.name}: Valid (${field.boundingBoxes?.length || 0} bounding boxes)`);
    }
  });
  
  const conflicts = detectFieldMappingConflicts(fields);
  if (conflicts.conflicts.length > 0) {
    console.warn('⚠️ Field Mapping Conflicts:', conflicts.conflicts);
  }
  
  console.groupEnd();
}